/**
 * Feature Flags System
 *
 * Controls access to Lite vs Comprehensive tier features.
 * Supports both integrated (SMS Pro) and standalone deployment.
 *
 * @module config/featureFlags
 */

export const FeatureFlags = {

  // =========================================================================
  // DEPLOYMENT CONTEXT DETECTION
  // =========================================================================

  /**
   * Check if running as standalone app
   * @returns {boolean}
   */
  isStandalone: () => {
    return !window.__SMS_PRO_CONTEXT__;
  },

  /**
   * Check if running integrated in SMS Pro
   * @returns {boolean}
   */
  isIntegrated: () => {
    return !!window.__SMS_PRO_CONTEXT__;
  },

  // =========================================================================
  // TIER DETECTION
  // =========================================================================

  /**
   * Check if user has Lite tier access
   * @param {Object} user - Current user object
   * @returns {boolean}
   */
  isLiteTier: (user) => {
    // Standalone is always Lite
    if (FeatureFlags.isStandalone()) {
      return true;
    }

    // Integrated: Standard subscription or no subscription = Lite
    if (!user || !user.subscription) {
      return true;
    }

    return user.subscription === 'standard';
  },

  /**
   * Check if user has Comprehensive tier access
   * @param {Object} user - Current user object
   * @returns {boolean}
   */
  isComprehensiveTier: (user) => {
    // Standalone never has Comprehensive
    if (FeatureFlags.isStandalone()) {
      return false;
    }

    // Integrated: Enterprise subscription only
    if (!user || !user.subscription) {
      return false;
    }

    return user.subscription === 'enterprise';
  },

  // =========================================================================
  // FEATURE CHECKS
  // =========================================================================

  features: {

    // -----------------------------------------------------------------------
    // LITE TIER FEATURES (Always Available)
    // -----------------------------------------------------------------------

    /**
     * Basic 5-step wizard
     * @returns {boolean}
     */
    basicWizard: () => true,

    /**
     * 5x5 risk matrix
     * @returns {boolean}
     */
    riskMatrix: () => true,

    /**
     * PDF export
     * @returns {boolean}
     */
    pdfExport: () => true,

    /**
     * Basic dashboard
     * @returns {boolean}
     */
    basicDashboard: () => true,

    /**
     * Review cycle tracking
     * @returns {boolean}
     */
    reviewTracking: () => true,

    /**
     * 44 workboat tasks (WBC3)
     * @returns {boolean}
     */
    workboatTasks: () => true,

    // -----------------------------------------------------------------------
    // COMPREHENSIVE TIER FEATURES (Enterprise Only)
    // -----------------------------------------------------------------------

    /**
     * ISM Code compliant approval workflow
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    approvalWorkflow: (user) => {
      return FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * Crew acknowledgment with signatures
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    crewAcknowledgment: (user) => {
      return FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * Advanced analytics dashboard
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    advancedAnalytics: (user) => {
      return FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * 100+ merchant vessel tasks
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    merchantVesselTasks: (user) => {
      return FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * Dynamic risk assessment (mobile)
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    dynamicRA: (user) => {
      return FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * Permit-to-work integration
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    permitToWork: (user) => {
      return FeatureFlags.isComprehensiveTier(user);
    },

    // -----------------------------------------------------------------------
    // INTEGRATION FEATURES (Enterprise + Integrated Only)
    // -----------------------------------------------------------------------

    /**
     * Equipment module integration
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    equipmentIntegration: (user) => {
      return FeatureFlags.isIntegrated() && FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * Incident module integration
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    incidentIntegration: (user) => {
      return FeatureFlags.isIntegrated() && FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * Drill module integration
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    drillIntegration: (user) => {
      return FeatureFlags.isIntegrated() && FeatureFlags.isComprehensiveTier(user);
    },

    /**
     * Crew module integration
     * @param {Object} user - Current user
     * @returns {boolean}
     */
    crewIntegration: (user) => {
      return FeatureFlags.isIntegrated() && FeatureFlags.isComprehensiveTier(user);
    }
  },

  // =========================================================================
  // HELPER METHODS
  // =========================================================================

  /**
   * Get user-friendly feature description for upgrade prompts
   * @param {string} featureName - Feature identifier
   * @returns {string}
   */
  getFeatureDescription: (featureName) => {
    const descriptions = {
      approvalWorkflow: 'ISM Code compliant approval workflow with senior management sign-off',
      crewAcknowledgment: 'Crew acknowledgment system with digital signatures',
      advancedAnalytics: 'Advanced analytics dashboard with risk trending and insights',
      merchantVesselTasks: '100+ merchant vessel task templates',
      dynamicRA: 'Mobile dynamic risk assessment for real-time hazard evaluation',
      permitToWork: 'Permit-to-work system integration',
      equipmentIntegration: 'Link risk assessments to equipment and maintenance',
      incidentIntegration: 'Create risk assessments from incident investigations',
      drillIntegration: 'Link emergency drills to risk controls',
      crewIntegration: 'Assign crew members to risk actions and track training'
    };

    return descriptions[featureName] || 'Premium feature';
  },

  /**
   * Get upgrade message for feature
   * @param {string} featureName - Feature identifier
   * @returns {string}
   */
  getUpgradeMessage: (featureName) => {
    if (FeatureFlags.isStandalone()) {
      return `Upgrade to FleetSkipper Enterprise to access ${FeatureFlags.getFeatureDescription(featureName)}`;
    }
    return `Upgrade to Enterprise tier to access ${FeatureFlags.getFeatureDescription(featureName)}`;
  }
};
